import { useLocale } from '@lessonup/client-integration';
import {
  Banner,
  BodyText,
  Button,
  color,
  IconBook,
  IconFile,
  InputValidation,
  Introduction,
  SelectField,
  SpaceBetween,
  spacing,
  styled,
  TextArea,
  TextElement,
  UseFormRegister,
  UseFormWatch,
} from '@lessonup/ui-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FullPageBodyAsideLayout } from '../../../../../components/FullPageFlow/FullPageBodyAsideLayout';
import {
  FullPageBodySection,
  FullPageBodySectionTransparent,
} from '../../../../../components/FullPageFlow/FullPageBodySection';
import { FullPageBodyAside } from '../../../../../components/FullPageFlow/FullPageFlowAside';
import { Language, MaiaExample } from '../../../../../types/graphql/types.generated';
import { getLanguageOptions } from '../../../../../utils/language/languageOptions';
import { ComprehensionLevelSelector } from '../../../../AiToolbox/helpers/ComprehensionLevelSelector';
import { giveSelectionOptions } from '../../../dataComponents/utils';
import { ContextUpload, OptionalLessonSettings } from '../../../model';
import { OptionalLessonSettingsList } from '../../OptionalLessonSettingsList';
import { SidebarChooseMaiaMode } from '../../SidebarChooseMaiaMode';
import { ExamplesSection } from './ExamplesSection';
import { getBodyText, getHeadline, LessonPromptStepMode } from './LessonPromptStep.utils';
import { UploadImage } from './UploadImage';

export interface LessonPromptFormInputs {
  prompt: string;
  slidesToGenerate: number;
  language: Language;
  comprehensionLevel: string;
  mode: LessonPromptStepMode;
}

interface LessonPromptStepProps {
  register: UseFormRegister<LessonPromptFormInputs>;
  watch: UseFormWatch<LessonPromptFormInputs>;
  promptCharacterLimit: number;
  promptMinCharacterLimit?: number;
  optionalLessonSettings: OptionalLessonSettings;
  setOptionalLessonSettings: React.Dispatch<React.SetStateAction<OptionalLessonSettings>>;
  maiaExamples?: MaiaExample[];
  selectedUploadExample?: string;
  contextUploads: ContextUpload[];
  setContextUploads: React.Dispatch<React.SetStateAction<ContextUpload[]>>;
  handleMaiaModeSwitch: () => void;
  onExamplesClick: (examplePrompt: string) => void;
}

export const LessonPromptStep = ({
  register,
  watch,
  promptCharacterLimit,
  promptMinCharacterLimit = 0,
  optionalLessonSettings,
  setOptionalLessonSettings,
  maiaExamples,
  selectedUploadExample,
  contextUploads,
  setContextUploads,
  handleMaiaModeSwitch,
  onExamplesClick,
}: LessonPromptStepProps) => {
  const { t } = useTranslation('chatGPT');
  const { language } = useLocale();
  const languageOptions = getLanguageOptions(t);
  const promptCharacterLimitForWarning = promptCharacterLimit - 500;
  const promptInputLength = watch('prompt')?.length;
  const mode = watch('mode');
  const isContextMode = mode === 'context';
  const isImageContextMode = mode === 'imageContext';

  const getPromptValidation = (): InputValidation | undefined => {
    if (isContextMode) {
      if (promptInputLength > 0 && promptInputLength < promptMinCharacterLimit) {
        return {
          type: 'negative',
          message: t('promptCharactersMinWarning', { promptInputLength, promptMinCharacterLimit }),
        };
      }

      if (promptInputLength > promptCharacterLimit) {
        return {
          type: 'negative',
          message: t('promptCharactersWarning', { promptInputLength, promptCharacterLimit }),
        };
      }

      if (promptInputLength >= promptCharacterLimitForWarning) {
        return {
          type: 'positive',
          message: t('promptCharactersWarning', { promptInputLength, promptCharacterLimit }),
        };
      }
    } else {
      if (promptInputLength >= promptCharacterLimitForWarning) {
        return {
          type: promptInputLength > promptCharacterLimit ? 'negative' : 'informative',
          message: t('promptCharactersWarning', { promptInputLength, promptCharacterLimit }),
        };
      }
    }

    return undefined;
  };

  return (
    <div>
      <FullPageBodySectionTransparent>
        <Introduction
          alignment="center"
          size="M"
          headline={getHeadline(mode, t)}
          bodyText={getBodyText(mode, language, t)}
        />
      </FullPageBodySectionTransparent>

      <FullPageBodyAsideLayout
        mainLabel={isContextMode ? t('contextStep.mainLabel') : t('promptStep.mainLabel')}
        asideLabel={t('optionalSettings')}
      >
        {isImageContextMode ? (
          <FullWidth>
            <UploadImage
              contextUploads={contextUploads}
              setContextUploads={setContextUploads}
              promptInputLength={promptInputLength}
              promptCharacterLimit={promptCharacterLimit}
              examples={maiaExamples}
              onExamplesClick={onExamplesClick}
              selectedUploadExample={selectedUploadExample}
            />
          </FullWidth>
        ) : (
          <FullPageBodySection>
            <FullWidth>
              <SpaceBetween direction="y" spacing={spacing.size16}>
                <div>
                  <TextElement textStyle="label" weight="bold" size="medium">
                    {t('contextStep.inputTitle')}
                  </TextElement>

                  <StyledSubText size="small">
                    {isContextMode ? t('contextStep.inputSubTitle') : t('promptStep.promptHelp')}
                  </StyledSubText>
                </div>
                <SpaceBetween direction="y" spacing={spacing.size32}>
                  <StyledTextArea
                    rows={20}
                    size="medium"
                    aria-label={t('contextStep.inputTitle')}
                    placeholder={
                      isContextMode ? t('contextStep.inputPromptExample') : t('promptStep.lessonPromptExample')
                    }
                    {...register('prompt')}
                    validation={getPromptValidation()}
                  />
                  {isContextMode
                    ? promptInputLength < promptMinCharacterLimit &&
                      promptInputLength > 0 && (
                        <Banner
                          priority="medium"
                          orientation="vertical"
                          alignment="start"
                          actionElement={
                            <Button onClick={handleMaiaModeSwitch} buttonType="neutral" iconStart={<IconFile />}>
                              {t('contextStep.switchMaiaModeCTA')}
                            </Button>
                          }
                          paragraph={t('contextStep.switchMaiaModeWarning')}
                        />
                      )
                    : promptInputLength > promptCharacterLimit && (
                        <Banner
                          priority="medium"
                          orientation="vertical"
                          alignment="start"
                          actionElement={
                            <Button onClick={handleMaiaModeSwitch} buttonType="neutral" iconStart={<IconBook />}>
                              {t('promptStep.switchMaiaModeCTA')}
                            </Button>
                          }
                          paragraph={t('promptStep.switchMaiaModeWarning')}
                        />
                      )}
                </SpaceBetween>
                <ExamplesSection
                  examples={maiaExamples}
                  isContextMode={isContextMode}
                  onExamplesClick={onExamplesClick}
                />
              </SpaceBetween>
            </FullWidth>
          </FullPageBodySection>
        )}

        <FullPageBodyAside>
          <SpaceBetween direction="y" spacing={spacing.size8}>
            {!isImageContextMode && (
              <SidebarChooseMaiaMode
                activeLessonType={isContextMode ? 'contextLesson' : 'lesson'}
                handleMaiaModeSwitch={handleMaiaModeSwitch}
              />
            )}
            <StyledPickerWrapper>
              <SpaceBetween direction="y" spacing={spacing.size8}>
                <div>
                  <TextElement textStyle="label" weight="bold" size="medium">
                    {t('languageOption')}
                  </TextElement>
                  <StyledSubText size="small">{t('languageOptionDescription')}</StyledSubText>
                </div>
                <SelectField
                  size="medium"
                  aria-label={t('languageOption')}
                  {...register('language')}
                  options={languageOptions}
                  defaultValue={language.toUpperCase()}
                />
              </SpaceBetween>
            </StyledPickerWrapper>
            <StyledPickerWrapper>
              <ComprehensionLevelSelector<LessonPromptFormInputs> register={register} />
            </StyledPickerWrapper>
            <StyledPickerWrapper>
              <SelectField<number>
                size="medium"
                label={t('slideCount')}
                {...register('slidesToGenerate')}
                options={giveSelectionOptions(20)}
                defaultValue={8}
              />
            </StyledPickerWrapper>
            <OptionalLessonSettingsList
              currentSettings={optionalLessonSettings}
              setOptionalLessonSettings={setOptionalLessonSettings}
              showSetting={{
                enableTeacherNotes: false,
                enablePreviousKnowledge: true,
                enableExitTicket: true,
                enableLearningPhase: false,
              }}
            />
          </SpaceBetween>
        </FullPageBodyAside>
      </FullPageBodyAsideLayout>
    </div>
  );
};

const StyledSubText = styled(BodyText)`
  color: ${color.additional.disabled.text};
`;

const StyledPickerWrapper = styled.div`
  padding: ${spacing.size8} 0;
`;

const StyledTextArea = styled(TextArea)`
  width: 100%;
`;

const FullWidth = styled.div`
  width: 100%;
`;
